/**
 * Action Helper Utilities
 * Provides convenient methods for working with the dynamic event system
 */

/**
 * Trigger an action with data
 * @param {string} actionName - The action name (with or without 'run' prefix)
 * @param {object} data - Data to pass to the action
 * @returns {Promise} Promise that resolves when action completes
 */
export const triggerAction = (actionName, data = {}) => {
    if (window.enableDebugMode) { console.log('Triggering action:', actionName, data); }
    return new Promise((resolve, reject) => {
        // Normalize action name
        const normalizedActionName = actionName.startsWith('run') ? actionName : `run${actionName.charAt(0).toUpperCase() + actionName.slice(1)}`;
        const eventName = getEventName(normalizedActionName);
        
        // Set up one-time listeners for success/error
        const successHandler = (event) => {
            cleanup();
            resolve(event.detail);
        };
        
        const errorHandler = (event) => {
            cleanup();
            reject(new Error(event.detail.error));
        };
        
        const cleanup = () => {
            window.removeEventListener(`${eventName}:success`, successHandler);
            window.removeEventListener(`${eventName}:error`, errorHandler);
        };
        
        // Add listeners
        window.addEventListener(`${eventName}:success`, successHandler, { once: true });
        window.addEventListener(`${eventName}:error`, errorHandler, { once: true });
        
        // Trigger the action
        if (window.enableDebugMode) { console.log('Dispatching event:', eventName, data); }
        window.dispatchEvent(new CustomEvent(eventName, {
            detail: data
        }));
        
        // Cleanup after timeout to prevent memory leaks
        setTimeout(cleanup, 10000); // 10 second timeout
    });
};

/**
 * Convert action name to event name
 * @param {string} actionName - The action name
 * @returns {string} The event name
 */
export const getEventName = (actionName) => {
    let eventAction = actionName;
    if (actionName.startsWith('run')) {
        eventAction = actionName.slice(3);
        eventAction = eventAction.charAt(0).toLowerCase() + eventAction.slice(1);
    }
    return `arc:action:${eventAction}`;
};

/**
 * Check if an action is available
 * @param {string} actionName - The action name to check
 * @returns {boolean} True if action is registered
 */
export const hasAction = (actionName) => {
    if (typeof window.getRegisteredActions === 'function') {
        const actions = window.getRegisteredActions();
        const eventName = getEventName(actionName);
        return actions.some(action => action.eventName === eventName);
    }
    return false;
};

/**
 * Get all available actions
 * @returns {Array} Array of action information
 */
export const getAvailableActions = () => {
    if (typeof window.getRegisteredActions === 'function') {
        return window.getRegisteredActions();
    }
    return [];
};

/**
 * Create a reusable action trigger function
 * @param {string} actionName - The action name
 * @returns {Function} Function that triggers the action
 */
export const createActionTrigger = (actionName) => {
    return (data = {}) => triggerAction(actionName, data);
};

/**
 * Batch trigger multiple actions
 * @param {Array} actions - Array of {actionName, data} objects
 * @param {boolean} parallel - Whether to run actions in parallel (default: false)
 * @returns {Promise} Promise that resolves when all actions complete
 */
export const triggerActions = async (actions, parallel = false) => {
    if (parallel) {
        return Promise.all(actions.map(({ actionName, data }) => 
            triggerAction(actionName, data)
        ));
    } else {
        const results = [];
        for (const { actionName, data } of actions) {
            try {
                const result = await triggerAction(actionName, data);
                results.push({ success: true, actionName, result });
            } catch (error) {
                results.push({ success: false, actionName, error: error.message });
            }
        }
        return results;
    }
};

/**
 * Add a global listener for all action events
 * @param {Function} callback - Callback function to handle events
 * @returns {Function} Cleanup function to remove the listener
 */
export const onAnyAction = (callback) => {
    const handler = (event) => {
        if (event.type.startsWith('arc:action:')) {
            callback(event);
        }
    };
    
    // Listen for all action events
    const eventTypes = ['success', 'error', ''];
    const listeners = [];
    
    // Get current actions and set up listeners
    const setupListeners = () => {
        const actions = getAvailableActions();
        actions.forEach(action => {
            eventTypes.forEach(suffix => {
                const eventName = suffix ? `${action.eventName}:${suffix}` : action.eventName;
                window.addEventListener(eventName, handler);
                listeners.push({ eventName, handler });
            });
        });
    };
    
    setupListeners();
    
    // Return cleanup function
    return () => {
        listeners.forEach(({ eventName, handler }) => {
            window.removeEventListener(eventName, handler);
        });
    };
};

/**
 * Convenience methods for common actions
 */
export const actions = {
    addToCart: (variantId, quantity = 1, properties = {}) => 
        triggerAction('runAddToCart', { variantId, quantity, properties }),
    
    showNotification: (message, type = 'info', duration = 3000) => 
        triggerAction('runShowNotification', { message, type, duration }),
    
    getCurrency: (country = 'AU') => 
        triggerAction('runGetCurrency', { country }),
    
    setCurrency: (countryCode, options = {}) => 
        triggerAction('runSetCurrency', { countryCode, ...options })
};

// Make utilities globally available
if (typeof window !== 'undefined') {
    window.arcActions = {
        trigger: triggerAction,
        has: hasAction,
        getAll: getAvailableActions,
        createTrigger: createActionTrigger,
        batch: triggerActions,
        onAny: onAnyAction,
        ...actions
    };
}

export default {
    triggerAction,
    getEventName,
    hasAction,
    getAvailableActions,
    createActionTrigger,
    triggerActions,
    onAnyAction,
    actions
};
